import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import {
  Zap, Trophy, TrendingUp, TrendingDown, Activity, Target,
  Crown, Medal, Award, BarChart3, RefreshCw, Layers, ArrowLeft,
  Filter, Coins, Landmark, Globe
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from 'recharts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const RANK_COLORS = {
  1: { bg: 'bg-amber-500/20', border: 'border-amber-500/50', text: 'text-amber-400', icon: Crown },
  2: { bg: 'bg-slate-400/20', border: 'border-slate-400/50', text: 'text-slate-300', icon: Medal },
  3: { bg: 'bg-orange-600/20', border: 'border-orange-600/50', text: 'text-orange-400', icon: Award },
};

const MARKET_TYPES = {
  all: { labelKey: 'competition.allMarkets', icon: Filter },
  crypto: { labelKey: 'competition.crypto', icon: Coins },
  equities: { labelKey: 'competition.equities', icon: Landmark },
  forex: { labelKey: 'competition.forex', icon: Globe },
  defi: { labelKey: 'competition.defi', icon: Activity },
  intangibles: { labelKey: 'competition.ipAssets', icon: Target },
};

const AGENT_MARKET_MAP = {
  'AGT-01': ['crypto'], 'AGT-02': ['crypto'], 'AGT-03': ['crypto', 'futures'],
  'AGT-04': ['defi'], 'AGT-05': ['crypto', 'forex'], 'AGT-06': ['crypto', 'equities'],
  'AGT-07': ['crypto', 'equities', 'forex'], 'AGT-08': ['crypto', 'equities'],
  'AGT-09': ['crypto', 'forex'], 'AGT-10': ['defi'], 'AGT-11': ['equities'],
  'AGT-12': ['crypto', 'equities'], 'AGT-13': ['crypto', 'equities', 'forex'],
  'AGT-14': ['equities'],
  'AGT-15': ['intangibles'],
};

function AgentLeaderboardCard({ agent, t }) {
  const rankStyle = RANK_COLORS[agent.rank] || { bg: 'bg-[#11141D]', border: 'border-white/10', text: 'text-white' };
  const RankIcon = rankStyle.icon || Target;

  const getTypeColor = (type) => {
    switch (type) {
      case 'ARBITRAGE': return 'text-blue-400 bg-blue-400/10';
      case 'ALPHA': return 'text-emerald-400 bg-emerald-400/10';
      case 'QUANT': return 'text-cyan-400 bg-cyan-400/10';
      case 'EXECUTION': return 'text-amber-400 bg-amber-400/10';
      default: return 'text-slate-400 bg-slate-400/10';
    }
  };

  const markets = AGENT_MARKET_MAP[agent.agent_id] || [];

  return (
    <div
      className={`${rankStyle.bg} border ${rankStyle.border} p-4 transition-all hover:scale-[1.01]`}
      data-testid={`leaderboard-agent-${agent.agent_id}`}
    >
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 flex items-center justify-center ${agent.rank <= 3 ? rankStyle.bg : 'bg-[#090B0F]'} border ${agent.rank <= 3 ? rankStyle.border : 'border-white/10'}`}>
            {agent.rank <= 3 ? (
              <RankIcon className={`w-5 h-5 ${rankStyle.text}`} />
            ) : (
              <span className="font-mono font-bold text-slate-400">#{agent.rank}</span>
            )}
          </div>
          <div>
            <h3 className={`font-bold text-sm ${agent.rank <= 3 ? rankStyle.text : 'text-white'}`}>{agent.name}</h3>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-mono text-xs text-slate-500">{agent.agent_id}</span>
              <span className={`text-xs px-1.5 py-0.5 font-mono ${getTypeColor(agent.type)}`}>{agent.type}</span>
            </div>
          </div>
        </div>
        <div className={`px-2 py-1 text-xs font-mono ${agent.status === 'LIVE' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-slate-500/20 text-slate-400'}`}>
          {agent.status}
        </div>
      </div>

      {/* Market tags */}
      <div className="flex gap-1 mb-3 flex-wrap">
        {markets.map(m => (
          <span key={m} className="text-[10px] font-mono px-1.5 py-0.5 bg-white/5 text-slate-400 border border-white/5">
            {m.toUpperCase()}
          </span>
        ))}
      </div>

      <div className="grid grid-cols-4 gap-3 mb-3">
        <div>
          <p className="text-xs text-slate-500 mb-0.5">{t('common.alpha')}</p>
          <p className={`font-mono font-bold text-sm ${agent.alpha_score >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
            {agent.alpha_score >= 0 ? '+' : ''}{agent.alpha_score}
          </p>
        </div>
        <div>
          <p className="text-xs text-slate-500 mb-0.5">{t('common.return')}</p>
          <p className={`font-mono font-bold text-sm ${agent.total_return >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
            {agent.total_return >= 0 ? '+' : ''}{agent.total_return}%
          </p>
        </div>
        <div>
          <p className="text-xs text-slate-500 mb-0.5">{t('common.sharpe')}</p>
          <p className="font-mono font-bold text-sm text-white">{agent.sharpe_ratio}</p>
        </div>
        <div>
          <p className="text-xs text-slate-500 mb-0.5">{t('common.winRate')}</p>
          <p className="font-mono font-bold text-sm text-white">{agent.win_rate}%</p>
        </div>
      </div>

      <div className="flex items-center justify-between text-xs">
        <span className="text-slate-500">
          P&L: <span className={agent.current_pnl >= 0 ? 'text-emerald-400' : 'text-red-400'}>
            ${agent.current_pnl?.toLocaleString()}
          </span>
        </span>
        <span className="text-slate-500">
          Trades: <span className="text-white">{agent.total_trades}</span>
        </span>
        <span className="text-slate-500">
          Capital: <span className="text-white">${(agent.capital_allocated / 1000).toFixed(0)}K</span>
        </span>
      </div>
    </div>
  );
}

export default function Competition() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [leaderboard, setLeaderboard] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [rebalancing, setRebalancing] = useState(false);
  const [marketFilter, setMarketFilter] = useState('all');

  const fetchData = useCallback(async () => {
    try {
      const [lbRes, statsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/competition/leaderboard`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/competition/stats`, { withCredentials: true })
      ]);
      setLeaderboard(lbRes.data.leaderboard || []);
      setStats({
        ...statsRes.data,
        totalAlpha: lbRes.data.total_alpha_generated,
        liveAgents: lbRes.data.live_agents,
        bestPerformer: lbRes.data.best_performer
      });
    } catch (err) {
      console.error('Error fetching competition data:', err);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 15000);
    return () => clearInterval(interval);
  }, [fetchData]);

  const handleRebalance = async () => {
    setRebalancing(true);
    try {
      await axios.post(`${BACKEND_URL}/api/competition/rebalance`, {}, { withCredentials: true });
      await fetchData();
    } catch (err) {
      console.error('Error rebalancing:', err);
    }
    setRebalancing(false);
  };

  const filteredLeaderboard = leaderboard.filter(agent => {
    if (marketFilter === 'all') return true;
    const markets = AGENT_MARKET_MAP[agent.agent_id] || [];
    return markets.includes(marketFilter);
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-[#030508] flex items-center justify-center">
        <div className="text-center">
          <Trophy className="w-12 h-12 text-amber-500 animate-pulse mx-auto mb-4" />
          <p className="text-slate-400 font-mono">{t('competition.loading')}</p>
        </div>
      </div>
    );
  }

  const chartData = filteredLeaderboard.slice(0, 10).map(agent => ({
    name: agent.agent_id,
    alpha: agent.alpha_score,
    return: agent.total_return
  }));

  return (
    <div className="min-h-screen bg-[#030508]" data-testid="competition-page">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-[#030508]/80 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-[1920px] mx-auto px-4 md:px-6 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/dashboard" className="text-slate-400 hover:text-white transition-colors" data-testid="back-to-dashboard">
                <ArrowLeft className="w-5 h-5" />
              </Link>
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-amber-500 flex items-center justify-center">
                  <Trophy className="w-5 h-5 text-[#030508]" />
                </div>
                <span className="text-xl font-black tracking-tight text-white" style={{ fontFamily: 'Chivo' }}>
                  Agent <span className="text-amber-500">{t('competition.arena')}</span>
                </span>
              </div>
            </div>

            <button onClick={handleRebalance} disabled={rebalancing}
              className="flex items-center gap-2 px-4 py-2 bg-amber-500/20 text-amber-400 font-mono text-sm hover:bg-amber-500/30 transition-colors disabled:opacity-50"
              data-testid="rebalance-btn">
              <Layers className={`w-4 h-4 ${rebalancing ? 'animate-spin' : ''}`} />
              {rebalancing ? t('competition.rebalancing') : t('competition.autoRebalance')}
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-[1920px] mx-auto p-4 md:p-6 space-y-6">
        {/* Stats Overview */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4" data-testid="competition-stats">
          <div className="bg-[#090B0F] border border-amber-500/30 p-4">
            <p className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-1">{t('competition.bestAgent')}</p>
            <p className="text-lg font-bold text-amber-400 truncate" data-testid="best-performer">{stats?.bestPerformer?.name || '-'}</p>
          </div>
          <div className="bg-[#090B0F] border border-white/10 p-4">
            <p className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-1">{t('competition.alphaTotal')}</p>
            <p className={`text-xl font-bold font-mono ${(stats?.totalAlpha ?? 0) >= 0 ? 'text-emerald-400' : 'text-red-400'}`} data-testid="total-alpha">
              ${stats?.totalAlpha?.toLocaleString() || 0}
            </p>
          </div>
          <div className="bg-[#090B0F] border border-white/10 p-4">
            <p className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-1">{t('competition.liveAgents')}</p>
            <p className="text-xl font-bold text-white font-mono">{stats?.liveAgents || 0}/14</p>
          </div>
          <div className="bg-[#090B0F] border border-white/10 p-4">
            <p className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-1">{t('competition.signalsToday')}</p>
            <p className="text-xl font-bold text-white font-mono">{stats?.total_signals_today || 0}</p>
          </div>
          <div className="bg-[#090B0F] border border-white/10 p-4">
            <p className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-1">{t('competition.markets')}</p>
            <p className="text-xl font-bold text-white font-mono">{stats?.markets_active?.length || 0}</p>
          </div>
        </div>

        {/* Market Filter */}
        <div className="flex items-center gap-2" data-testid="market-filter">
          <span className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold mr-2">{t('competition.filterByMarket')}</span>
          {Object.entries(MARKET_TYPES).map(([key, { labelKey, icon: Icon }]) => (
            <button
              key={key}
              onClick={() => setMarketFilter(key)}
              className={`flex items-center gap-1.5 px-3 py-1.5 font-mono text-xs transition-all border ${marketFilter === key
                ? 'border-amber-500/50 bg-amber-500/10 text-amber-400'
                : 'border-white/10 text-slate-500 hover:text-white hover:border-white/20'
                }`}
              data-testid={`filter-${key}`}
            >
              <Icon className="w-3.5 h-3.5" />
              {t(labelKey)}
            </button>
          ))}
        </div>

        {/* Alpha Score Chart */}
        <div className="bg-[#090B0F] border border-white/10 p-4 md:p-6">
          <div className="flex items-center gap-2 mb-4">
            <BarChart3 className="w-5 h-5 text-amber-500" />
            <h3 className="text-sm uppercase tracking-[0.2em] font-semibold text-white">{t('competition.alphaScore')}</h3>
            <span className="text-xs text-slate-500 ml-2">{filteredLeaderboard.length} {t('competition.agentsCount')}</span>
          </div>
          <div className="h-[200px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData}>
                <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fill: '#64748B', fontSize: 10 }} />
                <YAxis axisLine={false} tickLine={false} tick={{ fill: '#64748B', fontSize: 10 }} />
                <Tooltip contentStyle={{ backgroundColor: '#11141D', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 0 }} />
                <Bar dataKey="alpha" radius={[2, 2, 0, 0]}>
                  {chartData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.alpha >= 0 ? '#10B981' : '#EF4444'} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Leaderboard */}
        <div>
          <div className="flex items-center gap-2 mb-4">
            <Trophy className="w-5 h-5 text-amber-500" />
            <h3 className="text-sm uppercase tracking-[0.2em] font-semibold text-white">{t('competition.leaderboard')}</h3>
            <span className="text-xs text-slate-500 ml-2">{t('competition.sortedByAlpha')}</span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="leaderboard-grid">
            {filteredLeaderboard.map((agent) => (
              <AgentLeaderboardCard key={agent.agent_id} agent={agent} t={t} />
            ))}
          </div>

          {filteredLeaderboard.length === 0 && (
            <div className="text-center py-12">
              <Filter className="w-8 h-8 text-slate-600 mx-auto mb-3" />
              <p className="text-slate-500 font-mono text-sm">{t('competition.noAgentsMarket')}</p>
            </div>
          )}
        </div>

        {/* Active Markets */}
        {stats?.markets_active && stats.markets_active.length > 0 && (
          <div className="bg-[#090B0F] border border-white/10 p-4" data-testid="active-markets">
            <div className="flex items-center gap-2 mb-3">
              <Activity className="w-5 h-5 text-emerald-500" />
              <h3 className="text-sm uppercase tracking-[0.2em] font-semibold text-white">{t('competition.activeMarkets')}</h3>
            </div>
            <div className="flex flex-wrap gap-2">
              {stats.markets_active.map(market => (
                <span key={market} className="px-3 py-1 bg-emerald-500/10 text-emerald-400 text-xs font-mono border border-emerald-500/20">
                  {market}
                </span>
              ))}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
